// Informes consolidados por puesto (Anexo 1 GT 5, GT 6 y GT 7): cada puesto con el estado de sus tres informes y el
// PDF consolidado de todos los puestos por tipo. Se abre desde Puestos de Trabajo (módulo oculto para clientes).

import React, { useCallback, useEffect, useState } from 'react';
import { aprobacionParaPdf, cargarDatosEmpresa, entradasPuestos, evaluacionesDelInforme, medidasDelPuesto, resumenPrioritaria, type DatosEmpresa } from '../../lib/informes-puesto/ip-datos';
import { estadoPuestos, type PuestoEstado } from '../../lib/informes-puesto/ip-checklist';
import { contenidoInforme, ETIQUETA_ESTADO_PUESTO, ultimaVersion } from '../../lib/informes-puesto/ip-logica';
import type { AprobacionPDF } from '../../lib/informes-puesto/ip-pdf';
import { CORTO_TIPO_INFORME, NOTA_PLAZOS, TIPOS_INFORME, type ContenidoInforme, type TipoInforme } from '../../lib/informes-puesto/ip-tipos';
import InformePuestoModal from './InformePuestoModal';
import { boton, chip, COLOR_ESTADO, msg, S } from './estilos';

// El PDF (jsPDF) se carga al usarlo.
const pdfs = () => import('../../lib/informes-puesto/ip-pdf');

export interface InformesPuestoPanelProps { companyId: string; empresa: string; puedeEditar: boolean }

export default function InformesPuestoPanel({ companyId, empresa, puedeEditar }: InformesPuestoPanelProps) {
  const [datos, setDatos] = useState<DatosEmpresa | null>(null);
  const [estados, setEstados] = useState<PuestoEstado[]>([]);
  const [prioritarios, setPrioritarios] = useState<Record<string, number> | null>(null);
  const [abierto, setAbierto] = useState<{ id: string; name: string; tipo: TipoInforme } | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setError('');
    try {
      const d = await cargarDatosEmpresa(companyId);
      setDatos(d);
      setEstados(estadoPuestos(await entradasPuestos(d), new Date()));
      const r = await resumenPrioritaria();
      setPrioritarios(r ? Object.fromEntries(r.por_puesto.map(x => [x.job_position_id, x.con_condicion])) : null);
    } catch (e) { setError(msg(e)); } finally { setCargando(false); }
  }, [companyId]);
  useEffect(() => { cargar(); }, [cargar]);

  const consolidado = async (tipo: TipoInforme) => {
    if (!datos) return;
    try {
      const informes: { contenido: ContenidoInforme; aprobacion: AprobacionPDF | null }[] = [];
      for (const p of datos.puestos) {
        const inf = datos.informes.find(i => i.job_position_id === p.id && i.kind === tipo);
        if (!inf) continue;
        const v = ultimaVersion(datos.versiones.filter(x => x.report_id === inf.id));
        if (v) { informes.push({ contenido: v.snapshot as unknown as ContenidoInforme, aprobacion: await aprobacionParaPdf(v) }); continue; }
        informes.push({ aprobacion: null, contenido: contenidoInforme({ informe: inf, puestoId: p.id, empresa, hoy: new Date(), expuestos: datos.activosPorPuesto[p.id] ?? 0,
          evaluaciones: evaluacionesDelInforme(datos, p.id, tipo), adjuntos: datos.adjuntos.filter(a => a.report_id === inf.id), medidas: medidasDelPuesto(datos, p.id),
          prioritarios: tipo === 'riesgos' && prioritarios ? prioritarios[p.id] ?? 0 : null }) });
      }
      if (!informes.length) { alert('Todavía no hay informes de este tipo.'); return; }
      const { generarConsolidadoPDF, nombreArchivo } = await pdfs();
      generarConsolidadoPDF(empresa, tipo, informes, new Date()).save(nombreArchivo(`Consolidado_${CORTO_TIPO_INFORME[tipo]}_${empresa}`));
    } catch (e) { setError(msg(e)); }
  };

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }} aria-label="Informes por puesto">
      <div style={S.bloque}>
        <h3 style={S.h3}>Informes consolidados por puesto</h3>
        <p style={S.nota}>Cada puesto reúne sus evaluaciones y mediciones, los adjuntos, las medidas por jerarquía de controles y las conclusiones; la versión aprobada se firma y queda sin cambios. {NOTA_PLAZOS}</p>
        <div style={S.botones}>
          {TIPOS_INFORME.map(t => <button key={t} style={boton()} onClick={() => consolidado(t)} disabled={!datos}>PDF consolidado: {CORTO_TIPO_INFORME[t]}</button>)}
        </div>
      </div>
      {error && <p style={S.error} role="alert">{error}</p>}
      {cargando && <p style={S.nota}>Cargando…</p>}
      {!cargando && !estados.length && <p style={S.nota}>Primero registra los puestos de trabajo.</p>}
      {estados.map(p => (
        <div key={p.id} style={S.bloque}>
          <div style={S.fila}>
            <div style={{ minWidth: 0, flex: '1 1 180px' }}>
              <div style={{ ...S.texto, fontWeight: 700 }}>{p.nombre}</div>
              <div style={S.nota}>{p.activos === 1 ? '1 trabajador activo' : `${p.activos} trabajadores activos`}</div>
            </div>
          </div>
          <div style={S.rejilla}>
            {TIPOS_INFORME.map(t => {
              const r = p.resultado[t];
              return (
                <button key={t} style={{ ...boton(), flex: '1 1 150px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}
                  onClick={() => setAbierto({ id: p.id, name: p.nombre, tipo: t })}>
                  <span>{CORTO_TIPO_INFORME[t]}</span>
                  <span style={chip(COLOR_ESTADO[r.estado])}>{ETIQUETA_ESTADO_PUESTO[r.estado]}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {abierto && datos && (
        <InformePuestoModal companyId={companyId} empresa={empresa} datos={datos} puesto={{ id: abierto.id, name: abierto.name }} tipoInicial={abierto.tipo}
          prioritarios={prioritarios ? prioritarios[abierto.id] ?? 0 : null} puedeEditar={puedeEditar} onCambio={cargar} onCerrar={() => setAbierto(null)} />
      )}
    </section>
  );
}
